import { createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { appCookiesStorage, showApiErrors } from '@/utils';
import { AuthState, AUTH_SLICE_NAME, initialState } from './models';
import { resetPassword, logIn, forgotPassword, logout } from './actionCreators';
import { AUTHORIZATION_TOKEN_EXPIRES, ResponseStatusCodes } from '@/constants/common';
import { COOKIE_KEYS } from '@/constants/cookies';
import { IApiError } from '@/models/apiError.model';

export const slice = createSlice({
  name: AUTH_SLICE_NAME,
  initialState,
  reducers: {
    setIsAuthorized(state, action: PayloadAction<boolean>) {
      state.isAuthorized = action.payload;
    },
    resetAuthState() {
      return { ...initialState };
    },
    logOut() {
      appCookiesStorage.removeItem(COOKIE_KEYS.AUTH_TOKEN);
      localStorage.clear();
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(isFulfilled(logIn), (state: AuthState, action) => {
        const { payload } = action;

        if (!payload.data.access_token) {
          return;
        }

        state.isLoading = false;
        state.isAuthorized = true;
        state.authToken = payload.data.access_token;

        appCookiesStorage.setItem(COOKIE_KEYS.AUTH_TOKEN, payload.data.access_token, {
          expires: AUTHORIZATION_TOKEN_EXPIRES,
        });
      })
      .addMatcher(isFulfilled(logout), (state: AuthState) => {
        state.isLoading = false;
        state.isAuthorized = false;

        appCookiesStorage.setItem(COOKIE_KEYS.AUTH_TOKEN, '', {
          expires: AUTHORIZATION_TOKEN_EXPIRES,
        });
      })
      .addMatcher(isFulfilled(forgotPassword, resetPassword, logout), (state: AuthState) => {
        state.isLoading = false;
      })
      .addMatcher(isPending(forgotPassword, resetPassword, logout), (state: AuthState) => {
        state.isLoading = true;
        state.error = null;
      })
      .addMatcher(isRejected(forgotPassword, resetPassword), (state: AuthState, action) => {
        const { error } = action;
        state.isLoading = false;
        state.error = error;

        showApiErrors(error);
      })
      .addMatcher(isRejected(logIn), (state: AuthState, action) => {
        const { error } = action;
        showApiErrors(error);

        if ((error as IApiError)?.status === ResponseStatusCodes.NOT_AUTHORIZED) {
          state.user = null;
          state.isAuthorized = false;
          state.isLoading = false;
          state.authToken = null;
          appCookiesStorage.removeItem(COOKIE_KEYS.AUTH_TOKEN);
        }
      })
      .addMatcher(isRejected(logout), (state: AuthState) => {
        state.isAuthorized = false;
        state.isLoading = false;
        state.user = null;
        state.authToken = null;
        appCookiesStorage.removeItem(COOKIE_KEYS.AUTH_TOKEN);
      });
  },
});

export const { setIsAuthorized, logOut, resetAuthState } = slice.actions;

export default slice.reducer;
